<template>
  <div
    v-click-outside="() => (isMenuVisible = false)"
    class="relative inline-flex w-full items-center overflow-hidden rounded-xl border border-white/10 bg-darkGray pl-3 p-2"
    :class="{'ring ring-primary-300' : isMenuVisible}"
    style="overflow: visible;"
  >
    <button
      class="flex cursor-pointer items-center justify-between gap-x-3 w-full"
      @click="isMenuVisible = !isMenuVisible"
      type="button"
    >
      <div v-if="!modelValue || !modelValue.length" class="flex items-center justify-between w-full"> 
        <span class="text-white/50">{{ placeholder }}</span>
        <BaseIcon
          name="expand_more"
          class="transition-transform"
          :class="{
            'rotate-180': isMenuVisible,
          }"
        />
      </div>
      <div v-else class="flex items-center justify-between w-full">
        <span class="text-white">{{ formatSelections }}</span>
        <BaseIcon
          name="expand_more"
          class="transition-transform"
          :class="{
            'rotate-180': isMenuVisible,
          }"
        />
      </div>
    </button>
    
    <BaseDropdownMenu v-show="isMenuVisible" class="absolute top-11 left-0 right-0 z-50">
      <div 
        v-for="option in options" 
        :key="option"
        class="px-3 py-2 cursor-pointer hover:bg-white/10 rounded-xl transition-colors"
        :style="{color: isSelected(option) ? '#00FFAF' : 'white'}" 
        @click="toggleOption(option)"
      >
        <div class="flex items-center justify-between">
          <span>{{ option }}</span>
          <BaseIcon 
            v-if="isSelected(option)" 
            name="check" 
            class="text-primary-300"
          />
        </div>
      </div>
    </BaseDropdownMenu>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  options: {
    type: Array,
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Select options'
  },
  limit: {
    type: Number,
    default: null // Limite per il DISPLAY, non per le selezioni
  }
});

import { useI18n } from 'vue-i18n'

const { t } = useI18n();

const emit = defineEmits(['update:modelValue']);

const isMenuVisible = ref(false);

const formatSelections = computed(() => {
  if (!props.modelValue || props.modelValue.length === 0) return '';
  
  if (props.modelValue.length === 1) {
    return props.modelValue[0];
  }
  
  if (props.limit && props.modelValue.length > props.limit) {
    const displayItems = props.modelValue.slice(0, props.limit);
    const remainingCount = props.modelValue.length - props.limit;
    return `${displayItems.join(', ')} +${remainingCount} ${t('more')}`;
  }
  
  if (props.modelValue.length <= 3) {
    return props.modelValue.join(', ');
  }
  
  return `${props.modelValue.length} selected`;
});

const isSelected = (option) => {
  return props.modelValue && props.modelValue.includes(option);
};

const toggleOption = (option) => {
  const current = props.modelValue ? [...props.modelValue] : [];
  const idx = current.indexOf(option);

  if (idx > -1) {
    current.splice(idx, 1);
  } else {
    current.push(option);
  }

  emit('update:modelValue', current);
};
</script>

<style lang="postcss" scoped>
@property --gradientColor {
  syntax: "<color>";
  initial-value: #121212;
  inherits: false;
}

.select-wrapper {
  @apply relative;

  .expand {
    @apply absolute right-[1px] px-1 pl-6 top-1/2 transform -translate-y-1/2 pointer-events-none;
    background: linear-gradient(to left, var(--gradientColor) 40%, transparent);
    transition: --gradientColor 0.2s, all 0.2s;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  .clear {
    @apply absolute right-8 top-1/2 transform -translate-y-1/2 bg-danger bg-opacity-15 rounded-full backdrop-blur p-0.5 text-danger cursor-pointer;
    font-size: 1rem;
    font-variation-settings: "FILL" 0, "wght" 600, "GRAD" -25, "opsz" 20;
  }
}
</style>